import { PagerVariant } from '@aurora/shared-client/components/common/Pager/enums';
import React, { useContext } from 'react';
import type { MessageViewVariant } from '../../../types/enums';
import { ItemType } from '../../../types/enums';
import type { ItemViewTypeAndProps } from '../../entities/types';
import {
  PaneledItemListBody,
  PaneledItemListFooter
} from '../../common/List/PaneledItemList/PaneledItemListWrapper';
import { useClassNameMapper } from 'react-bootstrap';
import localStyles from './IdeaSearchBeforePostWidget.module.pcss';
import useTranslation from '../../useTranslation';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import type {
  Board,
  MessageConstraints
} from '@aurora/shared-generated/types/graphql-schema-types';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import PaneledItemListItems from '../../common/List/PaneledItemList/PaneledItemListItems';
import type {
  MessageViewsQuery,
  MessageViewsQueryVariables,
  MessageViewFragment
} from '../../../types/graphql-types';
import messagesQuery from '../../messages/MessageViews.query.graphql';
import PaneledItemListPager from '../../common/List/PaneledItemList/PaneledItemListPager';
import NodeHeaderActionButton from '../../nodes/NodeHeaderActionButton/NodeHeaderActionButton';
import { TextAlignment } from '@aurora/shared-types/texts/enums';
import useEntityViewQuery from '../../useEntityViewQuery';
import type { IdeaSearchBeforePostWidgetProps } from '../IdeaSearchBeforePostWidgetEditor/types';

interface Props {
  /**
   * Props from the parent widget
   */
  widgetProps: IdeaSearchBeforePostWidgetProps;
  /**
   * Element to render the intro text when
   * there are results to display
   */
  introTextElement: React.FC;
  /**
   * Class name for the panel item list footer
   */
  footerClassname?: string;
  /**
   * Class name for the pager component
   */
  pagerClassname?: string;
  /**
   * Class name for the action button wrapper
   */
  actionPanelClassname?: string;
  /**
   * Class name for the action button intro text
   */
  actionPanelIntroClassname?: string;
}

/**
 * Idea search before post widget sample results
 *
 * @author rishabh.jivan
 */
const IdeaSearchBeforePostSampleResults: React.FC<Props> = props => {
  const {
    widgetProps,
    introTextElement: IntroTextElement,
    footerClassname,
    pagerClassname,
    actionPanelClassname,
    actionPanelIntroClassname
  } = props;
  const { listVariant, pagerVariant, viewVariant, pageSize, panelType, enablePagination } =
    widgetProps;
  const cx = useClassNameMapper(localStyles);
  const i18n = useTranslation(EndUserComponent.IDEA_SEARCH_BEFORE_POST_WIDGET);
  const { formatMessage, loading: textLoading } = i18n;
  const { contextNode } = useContext(AppContext);
  const contextNodeAsBoard: Board = contextNode as Board;

  const constraints: MessageConstraints = {
    boardId: { eq: contextNode.id },
    depth: { eq: 0 }
  };

  const variables = {
    constraints,
    first: pageSize,
    useKudosCount: true,
    useMessageStatus: true
  };

  const queryResult = useEntityViewQuery<
    ItemType.MESSAGE,
    MessageViewsQuery,
    MessageViewsQueryVariables
  >(module, ItemType.MESSAGE, viewVariant, messagesQuery, {
    variables
  });

  const { data, loading } = queryResult;

  const hasData = !!data;
  const hasItems = data?.messages.edges.length > 0;

  if (textLoading) {
    return null;
  }

  return (
    <>
      {hasItems && <IntroTextElement />}
      {(hasData || loading) && (
        <PaneledItemListBody>
          <PaneledItemListItems<
            MessageViewFragment,
            ItemType.MESSAGE,
            ItemViewTypeAndProps<ItemType.MESSAGE, MessageViewVariant.INLINE>,
            MessageViewsQuery,
            MessageViewsQueryVariables
          >
            type={ItemType.MESSAGE}
            variant={viewVariant}
            listVariant={listVariant}
            queryResult={queryResult}
            panel={panelType}
          />
        </PaneledItemListBody>
      )}
      {hasData && (
        <PaneledItemListFooter className={cx(footerClassname)}>
          {enablePagination && pagerVariant.type !== PagerVariant.NONE && (
            <PaneledItemListPager<MessageViewsQuery, MessageViewsQueryVariables>
              queryResult={queryResult}
              pagerVariant={pagerVariant}
              pageSize={pageSize}
              className={cx(pagerClassname)}
            />
          )}
          <div className={cx(actionPanelClassname)}>
            <h6 className={cx(actionPanelIntroClassname)}>
              {hasItems ? formatMessage('actionIntroText') : formatMessage('noResultsFoundText')}
            </h6>
            <NodeHeaderActionButton
              node={contextNodeAsBoard}
              actionButtonTitle={formatMessage('actionButtonText')}
              alignment={TextAlignment.CENTER}
            />
          </div>
        </PaneledItemListFooter>
      )}
    </>
  );
};

export default IdeaSearchBeforePostSampleResults;
